import { TaskTypeEnum } from '@sr-sdks/api-sdk-axios';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useParams } from 'react-router-dom';
import { queryOptionsGetTasksPaginated } from '../api/task/taskApi';
import { useTaskPartyNames } from '../hooks/useTaskPartyNames';
import {
  HOME_VISIBLE_TASKS,
  defaultTaskSortBy,
} from '../pages/v3/Tasks/taskTabs';
import { WorkspaceTask } from '../types/taskTypes';
import { EntityList } from './EntityList';
import { SingleQueryResults } from './QueryResults/SingleQueryResults';
import { SnapshotListSkeleton } from './SnapshotListSkeleton';
import { TaskCard, TaskCardSkeleton, buildTaskCardViewUrl } from './TaskCard';
import { TaskSnapshotViewAll } from './TaskSnapshotViewAll';

export type DraftRegistrationTask = WorkspaceTask;

const TASK_TYPE = TaskTypeEnum.DraftRegistration;

const DraftRegistrationSkeleton = () => (
  <SnapshotListSkeleton
    label="Loading drafts"
    hasTitle
    numCards={HOME_VISIBLE_TASKS}
    renderCard={() => <TaskCardSkeleton type={TASK_TYPE} />}
  />
);

const DraftRegistrationContent = ({
  tasks,
  totalTasks,
}: {
  tasks: DraftRegistrationTask[];
  totalTasks: number;
}) => {
  const { search } = useLocation();
  const { workspaceId } = useParams();
  const { getPartyNames, isWorkspaceTypePending } = useTaskPartyNames(
    tasks,
    workspaceId,
  );

  if (isWorkspaceTypePending) {
    return <DraftRegistrationSkeleton />;
  }

  return (
    <EntityList>
      <EntityList.Header>
        <EntityList.Title>Draft</EntityList.Title>
        <EntityList.Count count={totalTasks} />

        <TaskSnapshotViewAll
          segment="drafts"
          totalTasks={totalTasks}
          workspaceId={workspaceId}
        />
      </EntityList.Header>

      {tasks.length === 0 ? (
        <EntityList.Empty
          title="There are no drafts"
          description="Any incomplete registrations will be displayed as a draft."
        />
      ) : (
        <EntityList.Content>
          {tasks.map((task) => (
            <TaskCard
              key={task._id}
              task={task}
              type={TASK_TYPE}
              {...getPartyNames(task)}
              viewUrl={buildTaskCardViewUrl(task, TASK_TYPE, search)}
              workspaceId={workspaceId}
            />
          ))}
        </EntityList.Content>
      )}
    </EntityList>
  );
};

export const DraftRegistrationSnapshot = () => {
  const { workspaceId } = useParams();

  const tasksQuery = useQuery(
    queryOptionsGetTasksPaginated({
      pageSize: HOME_VISIBLE_TASKS,
      sortBy: defaultTaskSortBy(TaskTypeEnum.DraftRegistration),
      type: TaskTypeEnum.DraftRegistration,
      workspaceId,
    }),
  );

  return (
    <SingleQueryResults
      loadingFallback={<DraftRegistrationSkeleton />}
      query={tasksQuery}
    >
      {(tasksResponse) => {
        const tasks = (tasksResponse?.items as DraftRegistrationTask[]) || [];

        return (
          <DraftRegistrationContent
            tasks={tasks}
            totalTasks={tasksResponse?.meta?.totalItems || 0}
          />
        );
      }}
    </SingleQueryResults>
  );
};
